import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { NavMorph } from '../../components/layout/NavMorph';
import { Footer } from '../../components/layout/Footer';
import { GetInTouchModal } from '../../components/modals/GetInTouchModal';
import { legalContent } from '../../data/legalContent';

interface LegalDocPageProps {
  docId: string;
  title: string;
  updatedAt?: string;
}

export const LegalDocPage: React.FC<LegalDocPageProps> = ({
  docId,
  title,
  updatedAt = '—',
}) => {
  const [getInTouchOpen, setGetInTouchOpen] = useState(false);
  const handleGetInTouch = () => setGetInTouchOpen(true);

  const handleBackClick = () => {
    window.location.href = '/legal';
  };

  return (
    <div className="relative min-h-screen bg-black overflow-x-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Vignette */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(
              circle at 50% 20%,
              rgba(0, 0, 0, 0.3) 0%,
              rgba(0, 0, 0, 0.8) 50%,
              rgba(0, 0, 0, 0.95) 100%
            )`,
          }}
        />

        {/* Noise texture */}
        <div
          className="absolute inset-0 opacity-[0.03] mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
          }}
        />
      </div>

      {/* Navigation */}
      <NavMorph onGetInTouchClick={handleGetInTouch} />

      {/* Main content */}
      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="w-full max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <motion.div
            className="mb-8 md:mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <button
              onClick={handleBackClick}
              className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-[#28e675]/80 transition-colors duration-200 group"
            >
              <svg
                className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span>Espace juridique</span>
              <span className="text-white/30">/</span>
              <span className="text-white/60">{title}</span>
            </button>
          </motion.div>

          {/* Header */}
          <div className="mb-12 md:mb-16">
            <motion.h1
              className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white/95 mb-4 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {title}
            </motion.h1>

            <motion.p
              className="text-sm text-white/40 font-medium"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              Dernière mise à jour: {updatedAt}
            </motion.p>
          </div>

          {/* Content */}
          <motion.div
            className="prose prose-invert max-w-none"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.3,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            {legalContent[docId] ? (
              <div className="space-y-8">
                {legalContent[docId]}
              </div>
            ) : (
              <div className="p-8 md:p-12 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm">
                <div className="flex flex-col items-center text-center space-y-6">
                  <div className="w-16 h-16 rounded-full bg-[#28e675]/10 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-[#28e675]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>

                  <div className="space-y-3">
                    <h2 className="text-2xl font-semibold text-white/90">
                      Contenu à compléter
                    </h2>
                    <p className="text-white/60 leading-relaxed max-w-lg">
                      Ce document sera disponible prochainement. Nous travaillons à
                      finaliser son contenu pour vous fournir toutes les informations
                      nécessaires.
                    </p>
                  </div>

                  <button
                    onClick={handleBackClick}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.06] hover:bg-white/[0.10] border border-white/10 hover:border-white/20 text-white/70 hover:text-white/90 transition-all duration-300"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 19l-7-7m0 0l7-7m-7 7h18"
                      />
                    </svg>
                    <span>Retour à l'espace juridique</span>
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
      <GetInTouchModal open={getInTouchOpen} onClose={() => setGetInTouchOpen(false)} />
    </div>
  );
};
